import React from 'react';
import PropTypes from 'prop-types';
import './empty-state.scss';
import clsx from 'clsx';
import { Search } from 'react-feather';
import Button, { ButtonSizes, ButtonTypes } from '../button';
import FeaturedIcon, { FeaturedIconColors } from '../featured-icon';
import useTranslation from '../../../utils/translation-utils';

const EmptyStateTypes = {
  SEARCH: 'es-search',
  CUSTOM: 'es-custom',
};

const EmptyStateSizes = {
  SMALL: 'size-s',
  MEDIUM: 'size-m',
  LARGE: 'size-l',
};

const EmptyState = ({
  type, size, text, supportingText, renderIcon,
  primaryButtonLabel, renderPrimaryButtonIcon, onPrimaryClick,
  secondaryButtonLabel, renderSecondaryButtonIcon, onSecondaryClick, color,
}) => {
  const [getTranslation] = useTranslation();
  const classNames = clsx('empty-state-cont', type, size);

  const handlePrimaryClicked = () => {
    if (onPrimaryClick && typeof onPrimaryClick === 'function') {
      onPrimaryClick.call(null);
    }
  };

  const handleSecondaryClicked = () => {
    if (onSecondaryClick && typeof onSecondaryClick === 'function') {
      onSecondaryClick.call(null);
    }
  };

  const getIcon = () => {
    let iconType = null;
    let customRenderIcon = null;

    switch (type) {
      case EmptyStateTypes.SEARCH:
        iconType = Search;
        break;
      case EmptyStateTypes.CUSTOM:
        customRenderIcon = renderIcon;
        break;
      default:
        break;
    }

    if (iconType || customRenderIcon) {
      return (
        <FeaturedIcon
          type={iconType}
          renderIcon={customRenderIcon}
          size={size}
          color={color}
          className="es-icon"
        />
      );
    }
    return null;
  };

  const getButtons = () => {
    const buttonSize = size === EmptyStateSizes.LARGE ? ButtonSizes.MEDIUM : ButtonSizes.SMALL;

    const secondaryButton = secondaryButtonLabel
      ? (
        <Button
          label={secondaryButtonLabel}
          size={buttonSize}
          type={ButtonTypes.SECONDARY}
          renderIcon={renderSecondaryButtonIcon}
          onClick={() => handleSecondaryClicked()}
        />
      ) : null;

    const primaryButton = primaryButtonLabel
      ? (
        <Button
          label={primaryButtonLabel}
          size={buttonSize}
          type={ButtonTypes.PRIMARY}
          renderIcon={renderPrimaryButtonIcon}
          onClick={() => handlePrimaryClicked()}
        />
      ) : null;

    return (
      <div className="es-buttons">
        {secondaryButton}
        {primaryButton}
      </div>
    );
  };

  return (
    <div className={classNames}>
      {getIcon()}
      <div className="es-text">{getTranslation(text)}</div>
      {supportingText && (
        <div className="es-supporting-text">
          {getTranslation(supportingText)}
        </div>
      )}
      {getButtons()}
    </div>
  );
};

EmptyState.propTypes = {
  type: PropTypes.oneOf(Object.values(EmptyStateTypes)),
  size: PropTypes.oneOf(Object.values(EmptyStateSizes)),
  text: PropTypes.string,
  renderIcon: PropTypes.func,
  supportingText: PropTypes.string,
  primaryButtonLabel: PropTypes.string,
  renderPrimaryButtonIcon: PropTypes.func,
  secondaryButtonLabel: PropTypes.string,
  renderSecondaryButtonIcon: PropTypes.func,
  onPrimaryClick: PropTypes.func,
  onSecondaryClick: PropTypes.func,
  color: PropTypes.string,
};

EmptyState.defaultProps = {
  type: EmptyStateTypes.SEARCH,
  size: EmptyStateSizes.MEDIUM,
  text: '',
  renderIcon: null,
  supportingText: '',
  primaryButtonLabel: '',
  renderPrimaryButtonIcon: null,
  secondaryButtonLabel: '',
  renderSecondaryButtonIcon: null,
  onPrimaryClick: () => {
  },
  onSecondaryClick: () => {
  },
  color: FeaturedIconColors.PRIMARY,
};

export {
  EmptyState as default,
  EmptyStateTypes,
  EmptyStateSizes,
};
